<script setup>
/**
 * FAQGrouped — replaces the flat FAQ list with 3 thematic groups.
 *
 * Pattern: TimeCamp / Insightful — when you have ≥6 FAQs, grouping by
 * concern makes the page more navigable. Three columns on desktop, stacked
 * on mobile. Each column has its own accordion.
 *
 * Groups designed to mirror the 3 buyer concerns:
 *   - Producto: what does it do, how does it install
 *   - Privacidad: the ethical objection (the biggest blocker in our category)
 *   - Comercial: pricing, support, commitment
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Sparkles, ShieldCheck, Briefcase, Plus, Minus } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();

const groups = computed(() => [
  {
    id: "product",
    icon: Sparkles,
    title: t("landing.faq.groups.product.title"),
    items: ["what_is", "realtime", "deployment_time", "compatibility"],
  },
  {
    id: "privacy",
    icon: ShieldCheck,
    title: t("landing.faq.groups.privacy.title"),
    items: ["data_handling", "is_surveillance", "employee_aware"],
  },
  {
    id: "commercial",
    icon: Briefcase,
    title: t("landing.faq.groups.commercial.title"),
    items: ["trial", "support", "cancel"],
  },
]);

// Each group keeps its own expanded state so multiple groups can
// have an item open at the same time.
const expanded = ref({});
const toggle = (gid, key) => {
  const id = `${gid}:${key}`;
  expanded.value[id] = !expanded.value[id];
};
const isOpen = (gid, key) => !!expanded.value[`${gid}:${key}`];
</script>

<template>
  <section id="faq" class="py-24 bg-secondary/40 scroll-mt-20">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-14 max-w-2xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.faq.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.faq.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.faq.subtitle") }}
        </p>
      </AnimatedSection>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
        <AnimatedSection
          v-for="(group, gi) in groups"
          :key="group.id"
          animation="fade-up"
          :delay="gi * 100"
        >
          <div class="h-full">
            <!-- Group header -->
            <div class="flex items-center gap-3 mb-5">
              <div class="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
                <component :is="group.icon" class="w-4 h-4 text-accent" />
              </div>
              <h3 class="text-base font-bold text-foreground">
                {{ group.title }}
              </h3>
            </div>

            <!-- Items -->
            <ul class="space-y-2">
              <li
                v-for="key in group.items"
                :key="key"
                class="rounded-xl border border-border bg-card overflow-hidden"
              >
                <button
                  type="button"
                  class="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-muted/40 transition-colors"
                  @click="toggle(group.id, key)"
                >
                  <span class="text-sm font-semibold text-foreground">
                    {{ t(`landing.faq.items.${key}.question`) }}
                  </span>
                  <component
                    :is="isOpen(group.id, key) ? Minus : Plus"
                    class="w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform"
                  />
                </button>
                <Transition name="faq-fade">
                  <div v-if="isOpen(group.id, key)" class="px-4 pb-4">
                    <p class="text-sm text-muted-foreground leading-relaxed">
                      {{ t(`landing.faq.items.${key}.answer`) }}
                    </p>
                  </div>
                </Transition>
              </li>
            </ul>
          </div>
        </AnimatedSection>
      </div>
    </div>
  </section>
</template>

<style scoped>
.faq-fade-enter-active,
.faq-fade-leave-active {
  transition: opacity 0.2s ease, max-height 0.25s ease;
  overflow: hidden;
}
.faq-fade-enter-from,
.faq-fade-leave-to {
  opacity: 0;
  max-height: 0;
}
.faq-fade-enter-to,
.faq-fade-leave-from {
  opacity: 1;
  max-height: 400px;
}
</style>
